/**
 * Authentication Utilities for Background Services
 *
 * Provides safe auth handling for background services that run independently
 * of React components and need to manage token refresh manually.
 */

import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";

/**
 * Attempt to ensure valid authentication by refreshing if needed
 * Returns true if auth is valid, false if user is not authenticated
 */
export async function ensureAuthValid(): Promise<boolean> {
  try {
    // First check if we have a user
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError || !user) {
      logger.warn('[AuthUtils] No authenticated user found');
      return false;
    }

    // Try to get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      logger.warn('[AuthUtils] Error getting session:', sessionError.message);
      return false;
    }

    // If we have a session, it should be valid (autoRefreshToken should have handled it)
    if (session) {
      return true;
    }

    // No session, try to refresh
    logger.log('[AuthUtils] No active session, attempting refresh...');
    const { data: { session: refreshedSession }, error: refreshError } =
      await supabase.auth.refreshSession();

    if (refreshError || !refreshedSession) {
      logger.error('[AuthUtils] Failed to refresh session:', refreshError?.message);
      return false;
    }

    logger.log('[AuthUtils] Session refreshed successfully');
    return true;
  } catch (error) {
    logger.error('[AuthUtils] Unexpected error in ensureAuthValid:', error);
    return false;
  }
}

/**
 * Check if an error is an authentication-related error
 */
export function isAuthError(error: any): boolean {
  if (!error) return false;

  const errorMessage = error.message || error.toString() || '';
  const authErrorPatterns = [
    'authentication expired',
    'invalid token',
    'unauthorized',
    'jwt malformed',
    'jwt expired',
  ];

  return authErrorPatterns.some(pattern =>
    errorMessage.toLowerCase().includes(pattern)
  );
}

/**
 * Safely execute a Supabase query with auth handling
 * If auth fails, attempts refresh and retries once
 */
// PromiseLike, not Promise: callers hand this a Supabase query builder, which is a
// thenable but has no catch/finally. Requiring a full Promise made T fall back to
// unknown at every call site, so `data` lost its row type.
export async function executeWithAuthFallback<T>(
  operationName: string,
  queryFn: () => PromiseLike<{ data: T | null; error: any }>
): Promise<{ data: T | null; error: any; isAuthError: boolean }> {
  try {
    // First attempt
    const result = await queryFn();

    // Check if this is an auth error
    if (result.error && isAuthError(result.error)) {
      logger.warn(`[AuthUtils] Auth error in ${operationName}, attempting refresh...`);

      // Try to refresh auth
      const isValid = await ensureAuthValid();
      if (!isValid) {
        logger.error(
          `[AuthUtils] Auth refresh failed for ${operationName}, user needs to re-authenticate`
        );
        return { data: null, error: result.error, isAuthError: true };
      }

      // Retry the query after refresh
      logger.log(`[AuthUtils] Auth refreshed, retrying ${operationName}...`);
      const retryResult = await queryFn();

      if (retryResult.error && isAuthError(retryResult.error)) {
        logger.error(`[AuthUtils] Auth error persists in ${operationName} after refresh`);
        return { data: null, error: retryResult.error, isAuthError: true };
      }

      return { ...retryResult, isAuthError: false };
    }

    return { ...result, isAuthError: false };
  } catch (error) {
    logger.error(`[AuthUtils] Unexpected error in ${operationName}:`, error);
    return {
      data: null,
      error,
      isAuthError: false,
    };
  }
}
